import React from 'react';
import PropTypes from 'prop-types';
import {
	Divider,
	Grid2 as Grid,
	Box,
	TextField,
	MenuItem,
	FormControl,
	FormLabel,
	RadioGroup,
	Radio,
	FormControlLabel,
	InputLabel,
	Select,
	OutlinedInput,
	Checkbox,
	ListItemText,
	Autocomplete,
	Button,
} from '@mui/material';
import { RnDDialog } from 'components';
import { useIntl } from 'react-intl';

const weekArr = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
const BulkAddDialog = ({
	open,
	onClose,
	formData,
	classes,
	userGroups,
	departments,
	mediaFileValue,
	handleFormFieldChange,
	handleNextButton,
}) => {
	const intl = useIntl();

	return (
		<RnDDialog
			open={open}
			size="sm"
			title={intl.formatMessage({
				id: 'pages.MediaFiles.components.BulkAddToScreens.BulkAddDialogTitle',
			})}
			heightSize="lg"
			onClose={onClose}
			onActions={['Next'].map((item, index) => {
				return (
					<Button key={index} onClick={handleNextButton} variant="contained" color="primary">
						{item}
					</Button>
				);
			})}
		>
			{/* User Groups & Department */}
			<Box display="flex" marginTop={1} gap={2} mb={2}>
				<FormControl fullWidth>
					<Autocomplete
						multiple
						size="small"
						options={userGroups}
						value={userGroups.filter((g) => formData.selectedGroups.includes(g.id))}
						disableCloseOnSelect
						onChange={(event, newValue) => {
							const selectedIds = newValue.map((item) => item.id);
							handleFormFieldChange('selectedGroups', selectedIds);
						}}
						getOptionLabel={(option) => option.name}
						isOptionEqualToValue={(option, value) => option.id === value.id}
						renderOption={(props, option, { selected }) => (
							<MenuItem {...props} dense disableGutters sx={{ paddingY: 0.5 }}>
								<Checkbox checked={selected} sx={{ paddingY: 0.2, paddingX: 1 }} />
								<ListItemText primary={option.name} />
							</MenuItem>
						)}
						renderInput={(params) => <TextField {...params} label="Select User Groups" />}
					/>
				</FormControl>

				<FormControl fullWidth>
					<Autocomplete
						options={departments}
						size="small"
						value={departments.find((opt) => opt.id === formData.selectedDepartment) || null}
						onChange={(event, newValue) =>
							handleFormFieldChange('selectedDepartment', newValue?.id || '')
						}
						getOptionLabel={(option) => option.name}
						isOptionEqualToValue={(option, value) => option.id === value.id}
						renderOption={(props, option, { selected }) => (
							<MenuItem {...props} dense disableGutters sx={{ paddingY: 0.5 }}>
								<Radio checked={selected} value={option.id} sx={{ marginRight: 1, padding: 0 }} />
								<ListItemText primary={option.name} />
							</MenuItem>
						)}
						renderInput={(params) => <TextField {...params} label="Select a Department" />}
					/>
				</FormControl>
			</Box>
			<Grid container spacing={2} mb={2}>
				<Grid xs={6}>
					<TextField
						fullWidth
						label="Playlist Name"
						value={formData.playlistName}
						onChange={(e) => handleFormFieldChange('playlistName', e.target.value)}
					/>
				</Grid>
				<Grid xs={6} display="flex" alignItems="center">
					<FormControl>
						<FormLabel>Select Orientation</FormLabel>
						<RadioGroup
							row
							value={formData.orientation}
							onChange={(e) => handleFormFieldChange('orientation', e.target.value)}
						>
							<FormControlLabel value="landscape" control={<Radio />} label="Landscape" />
							<FormControlLabel value="portrait" control={<Radio />} label="Portrait" />
						</RadioGroup>
					</FormControl>
				</Grid>
			</Grid>

			<Divider sx={{ mb: 2 }} />

			<Box display="flex" gap={2}>
				<Box flex={4} display="flex" justifyContent="center" p={2}>
					{mediaFileValue.previewUrl ? (
						<Box
							component="img"
							src={mediaFileValue.previewUrl}
							alt="Preview"
							sx={{
								maxHeight: 200,
								width: 'auto',
								maxWidth: '100%',
								objectFit: 'contain',
							}}
						/>
					) : (
						<span>No Preview Available</span>
					)}
				</Box>

				<Box flex={6} display="flex" flexDirection="column" gap={2}>
					<TextField
						fullWidth
						label="Start Date"
						type="datetime-local"
						slotProps={{
							inputLabel: { shrink: true },
						}}
						value={formData.startDate}
						onChange={(e) => handleFormFieldChange('startDate', e.target.value)}
					/>

					<TextField
						fullWidth
						label="End Date"
						type="datetime-local"
						slotProps={{
							inputLabel: { shrink: true },
						}}
						value={formData.endDate}
						onChange={(e) => handleFormFieldChange('endDate', e.target.value)}
					/>

					<TextField
						fullWidth
						label="Daily Start"
						type="time"
						slotProps={{
							inputLabel: { shrink: true },
						}}
						value={formData.dailyStart}
						onChange={(e) => {
							let value = e.target.value;
							if (value && value.length === 5) value += ':00';
							handleFormFieldChange('dailyStart', value);
						}}
					/>

					<TextField
						fullWidth
						label="Daily End"
						type="time"
						slotProps={{
							inputLabel: { shrink: true },
						}}
						value={formData.dailyEnd}
						onChange={(e) => {
							let value = e.target.value;
							if (value && value.length === 5) value += ':00';
							handleFormFieldChange('dailyEnd', value);
						}}
					/>

					<FormControl fullWidth>
						<InputLabel>Days</InputLabel>
						<Select
							multiple
							value={formData.selectedDays}
							onChange={(e) => handleFormFieldChange('selectedDays', e.target.value)}
							input={<OutlinedInput label="Days" />}
							renderValue={(selected) => {
								if (selected.length === 0) return 'None selected';
								if (selected.length === weekArr.length) return `All selected (${selected.length})`;
								if (selected.length <= 2) return selected.join(', ');
								return `${selected.length} selected`;
							}}
						>
							{weekArr.map((name) => (
								<MenuItem key={name} value={name}>
									<Checkbox checked={formData.selectedDays.includes(name)} />
									<ListItemText primary={name} />
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<FormControl fullWidth>
						<InputLabel>Duration</InputLabel>
						<Select
							labelId="demo-simple-select-label"
							id="demo-simple-select"
							label="Duration"
							value={formData.selectedDuration}
							onChange={(e) => handleFormFieldChange('selectedDuration', e.target.value)}
						>
							{mediaFileValue.fileType.toUpperCase() === 'VIDEO' ? (
								<MenuItem key={mediaFileValue.duration} value={mediaFileValue.duration}>
									{mediaFileValue.duration} seconds
								</MenuItem>
							) : (
								Array.from({ length: 11 }, (_, i) => i * 5 + 10).map((sec) => (
									<MenuItem key={sec} value={sec} className={classes.filterMenuItem} dense>
										{sec} seconds
									</MenuItem>
								))
							)}
						</Select>
					</FormControl>
				</Box>
			</Box>
		</RnDDialog>
	);
};

BulkAddDialog.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	formData: PropTypes.object.isRequired,
	classes: PropTypes.object.isRequired,
	userGroups: PropTypes.array.isRequired,
	departments: PropTypes.array.isRequired,
	mediaFileValue: PropTypes.object.isRequired,
	handleFormFieldChange: PropTypes.func.isRequired,
	handleNextButton: PropTypes.func.isRequired,
};

export default BulkAddDialog;
